import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { vipAPI } from '../../../services/api';
import { useBrand } from '../../../context/BrandContext';
import { Spinner } from '../../ui/spinner';
import { Alert, AlertDescription } from '../../ui/alert';
import Pager from './Pager';
import { useContactChannels } from './ContactFormFields';
import { LogTable, LogTiles, LogToolbar } from './LogParts';
import { apiError } from './trackerView';
import { groupByDay, logFiltersFromSearch, logQuery, playerChipName, searchWithLogFilters } from './logView';

const SEARCH_DEBOUNCE_MS = 300;

// ContactLogTab lists every logged contact, newest first and grouped by day: the chosen manager's
// by default, or every manager's, over the last 30 days. Clicking a player shows only their
// contacts. The search box (player, email, ID or note text) stays out of the URL.
export default function ContactLogTab({ agent }) {
  const { activeBrand } = useBrand();
  const { channels } = useContactChannels(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = logFiltersFromSearch(searchParams);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const queryKey = searchWithLogFilters(filters).toString();
  const firstSearch = useRef(true);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  const setFilters = useCallback((patch) => {
    const next = { ...logFiltersFromSearch(searchParams), page: 1, ...patch };
    setSearchParams(searchWithLogFilters(next), { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (firstSearch.current) { firstSearch.current = false; return; }
    if (filters.page !== 1) setFilters({ page: 1 });
  }, [debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    const params = logQuery(logFiltersFromSearch(new URLSearchParams(queryKey)), agent, debouncedSearch);
    vipAPI.getContactLog(params, controller.signal)
      .then((res) => setData(res.data))
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(err.response?.status === 503 ? 'Data source unavailable' : apiError(err, 'Failed to load the contact log.'));
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [agent, activeBrand, queryKey, debouncedSearch]);

  const rows = data?.data || [];
  const groups = groupByDay(rows);

  return (
    <div className="space-y-5">
      <LogTiles summary={data?.summary} filters={filters} />
      <LogToolbar
        filters={filters} agent={agent} channels={channels} playerName={playerChipName(filters.player, rows)}
        search={search} onFilters={setFilters} onSearch={setSearch}
      />

      {error && <Alert variant="error"><AlertDescription>{error}</AlertDescription></Alert>}
      {loading && !data && <div className="flex justify-center py-8"><Spinner size="lg" /></div>}

      {data && (
        <div className="relative space-y-3">
          {loading && (
            <div className="absolute inset-0 z-10 flex items-start justify-center rounded-2xl bg-black/30 pt-8"><Spinner /></div>
          )}
          {rows.length === 0 ? (
            <div className="rounded-2xl border border-white/10 bg-surface px-5 py-12 text-center text-ink-dim">
              No contacts match these filters.
            </div>
          ) : (
            <LogTable groups={groups} onPlayer={(player) => setFilters({ player })} />
          )}
          <Pager page={filters.page} total={data.total} onPage={(page) => setFilters({ page })} />
        </div>
      )}
    </div>
  );
}
